import React, { useEffect, useState } from "react"
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
} from "reactstrap"
import Breadcrumb from "components/Common/Breadcrumb"
import { Link, useNavigate, useParams } from "react-router-dom"
import { useFormik } from "formik"
import * as Yup from "yup"
import toast from "react-hot-toast"
import Spinners from "components/Common/Spinner"
import {
  useInviteWealthPartnersMutation,
  useLazyGetWealthPartnerQuery,
  useUpdateWealthPartnerMutation,
} from "store/api/wealth-partner/wealth-partners.api"
import LoaderComponent from "components/loader"

const defaultValues = {
  entity_type: "INDIVIDUAL",
  constitution_of_business: "",
  name: "",
  email: "",
  pan_no: "",
}

const constitutionOfBusiness = [
  {
    label: "Individual/Proprietorship",
    value: "Individual/Proprietorship",
  },
  {
    label: "Private Limited Company",
    value: "Private Limited Company",
  },
  {
    label: "Public Limited Company",
    value: "Public Limited Company",
  },
  {
    label: "Limited Liability Partnership",
    value: "Limited Liability Partnership",
  },
  {
    label: "Partnership Firm",
    value: "Partnership Firm",
  },
  {
    label: "Hindu Undivided Family",
    value: "Hindu Undivided Family",
  },
]

const InviteWealthPartner = () => {
  const { id } = useParams()
  document.title = id ? "Wealth Partner" : "Invite Wealth Partner"
  const [invitePartner, { isLoading }] = useInviteWealthPartnersMutation()
  const [reInvitePartner, { isLoading: isUpdating }] =
    useUpdateWealthPartnerMutation()
  const [getResult, { isFetching }] = useLazyGetWealthPartnerQuery()
  const history = useNavigate()
  const [userData, setUserData] = useState(defaultValues)

  useEffect(() => {
    const getUser = async () => {
      const { data } = await getResult({ id }).unwrap()

      setUserData({
        entity_type: data.entity_type,
        constitution_of_business:
          data?.businessAdditionalDetails?.constitution_of_business ||
          "Individual/Proprietorship",
        name: data.name,
        email: data.email,
        pan_no: data.pan_no,
      })

      if (data.status === "KYC_PENDING") {
        history(`/wealth-partners/view/${id}`)
      }
    }
    if (id) {
      getUser()
    } else {
      setUserData({ ...defaultValues })
    }
  }, [id])

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: { ...userData },
    validationSchema: Yup.object({}),
    onSubmit: async (values) => {
      await onFormSubmit(values)
    },
  })

  const onFormSubmit = async (values) => {
    try {
      const { data, message, success } = id
        ? await reInvitePartner({ ...values, id }).unwrap()
        : await invitePartner(values).unwrap()
      if (success) {
        history("/wealth-partners")
        toast.success(message, { id: "toastMessage" })
      }
    } catch (error) {
      const data = error?.data
      if (data?.errors) {
        const { errors } = data
        validation.setErrors(errors)
      }

      toast.error(error?.data?.message, { id: "toastMessage" })
    }
  }

  useEffect(() => {
    if (
      validation?.values?.constitution_of_business ===
      "Individual/Proprietorship"
    ) {
      validation.setFieldValue("entity_type", "INDIVIDUAL")
    } else {
      validation.setFieldValue("entity_type", "COMPANY")
    }
  }, [validation.values])

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* Render Breadcrumb */}
          <Breadcrumb title={id ? "Wealth Partner" : "Invite Wealth Partner"} />

          <Row>
            <Col xl={12}>
              <Card>
                <CardBody>
                  {isFetching ? (
                    <LoaderComponent />
                  ) : (
                    <Form
                      className="form-horizontal"
                      onSubmit={(e) => {
                        e.preventDefault()
                        validation.handleSubmit()
                        return false
                      }}
                    >
                      <Row className="mb-2">
                        <Col xl={6} className="mb-2">
                          <Label className="form-label">Entity Type</Label>
                          <select
                            name="constitution_of_business"
                            className="form-select pageSize mb-2"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={
                              validation.values.constitution_of_business || ""
                            }
                          >
                            <option value="">Select Entity Type</option>
                            {constitutionOfBusiness.map((item, index) => (
                              <option key={index} value={item.value}>
                                {item.label}
                              </option>
                            ))}
                          </select>
                          {validation.touched.constitution_of_business &&
                          validation.errors.constitution_of_business ? (
                            <FormFeedback type="invalid">
                              {validation.errors.constitution_of_business}
                            </FormFeedback>
                          ) : null}
                        </Col>
                        <Col xl={6} className="mb-2">
                          <Label className="form-label">{"Entity Name"}</Label>
                          <Input
                            name="name"
                            className="form-control"
                            placeholder={"Enter Entity Name"}
                            type="text"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.name || ""}
                            invalid={
                              validation.touched.name && validation.errors.name
                                ? true
                                : false
                            }
                          />
                          {validation.touched.name && validation.errors.name ? (
                            <FormFeedback type="invalid">
                              {validation.errors.name}
                            </FormFeedback>
                          ) : null}
                        </Col>
                        <Col xl={6} className="mb-2">
                          <Label className="form-label">Email Address</Label>
                          <Input
                            name="email"
                            className="form-control"
                            placeholder="Enter Email Address"
                            type="text"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.email || ""}
                            invalid={
                              validation.touched.email &&
                              validation.errors.email
                                ? true
                                : false
                            }
                          />
                          {validation.touched.email &&
                          validation.errors.email ? (
                            <FormFeedback type="invalid">
                              {validation.errors.email}
                            </FormFeedback>
                          ) : null}{" "}
                        </Col>
                        <Col xl={6} className="mb-2">
                          <Label className="form-label">PAN</Label>
                          <Input
                            name="pan_no"
                            className="form-control"
                            placeholder="PAN"
                            type="text"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.pan_no || ""}
                            invalid={
                              validation.touched.pan_no &&
                              validation.errors.pan_no
                                ? true
                                : false
                            }
                          />
                          {validation.touched.pan_no &&
                          validation.errors.pan_no ? (
                            <FormFeedback type="invalid">
                              {validation.errors.pan_no}
                            </FormFeedback>
                          ) : null}
                        </Col>
                      </Row>

                      <div className="mt-3 py-1 button-wrapper">
                        <Link to={"/wealth-partners"}>
                          <button type="button" className="cancel">
                            Cancel
                          </button>
                        </Link>
                        <Button
                          type="submit"
                          color="primary"
                          disabled={isLoading || isUpdating}
                        >
                          {isLoading || isUpdating ? (
                            <Spinners />
                          ) : id ? (
                            "Resend Invite"
                          ) : (
                            "Send invite"
                          )}
                        </Button>
                      </div>
                    </Form>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default InviteWealthPartner
